/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { truncateValue } from 'src/core/utils/number';
import styled from 'styled-components';

type ConfusionMatrixColorValueScaleProps = {
    maxNumber: number;
};

const messages = defineMessages({
    colorLabel: {
        id: 'customText.confusionMatrixColorValueScale.colorLabel',
        defaultMessage: 'Cell colors:'
    }
});

const theme = getScopedTheme('ConfusionMatrixColorValueScale');

const OneValueSpan = styled.span({
    width: '1px',
    height: '4px',
    marginLeft: '-1px',
    backgroundColor: theme.valueColor
});

const ColoredSpan = styled.span<{ cellColor: string }>(props => ({
    width: '85px',
    height: '10px',
    backgroundColor: props.cellColor,
    msHighContrastAdjust: 'none',
    forcedColorAdjust: 'none'
}));

const containerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { margin: '30px 0 20px 0 !important', overflow: 'auto' }
});

export const ConfusionMatrixColorValueScale = (props: ConfusionMatrixColorValueScaleProps) => {
    const { maxNumber } = props;
    const { localize } = useLocalization();
    const getColorCell = (color: string, value: string) => (
        <Stack>
            <ColoredSpan cellColor={color} />
            <span style={{ width: '1px', height: '4px', backgroundColor: theme.valueColor }} />
            <LuisText>{value}</LuisText>
        </Stack>
    );

    return (
        <Stack styles={containerStyles} horizontal>
            <LuisText fontWeight={600} styles={{ root: { marginRight: '15px' } }}>
                {localize(messages.colorLabel)}
            </LuisText>
            {getColorCell(theme.cellColor0, '0.0')}
            {getColorCell(theme.cellColor2, `${truncateValue(0.2 * maxNumber, 1)}`)}
            {getColorCell(theme.cellColor4, `${truncateValue(0.4 * maxNumber, 1)}`)}
            {getColorCell(theme.cellColor6, `${truncateValue(0.6 * maxNumber, 1)}`)}
            {getColorCell(theme.cellColor8, `${truncateValue(0.8 * maxNumber, 1)}`)}
            <Stack styles={{ root: { marginTop: '10px' } }}>
                <OneValueSpan />
                <LuisText>{truncateValue(maxNumber, 2)}</LuisText>
            </Stack>
        </Stack>
    );
};
